import React, { useState, useRef } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useSelector } from "react-redux";
import { formatCount } from "../../../utils/numberFormat";

const HandwashingFacilityChart = () => {
  const handWashGraphFilter = useSelector(
    (state) => state.dashboard?.handWashGraphCount
  );

  const [showModal, setShowModal] = useState(false);
  const chartContainerRef = useRef(null);
  const modalChartContainerRef = useRef(null);


  const data = [
    { name: "Available", value: handWashGraphFilter?.Available || 0 },
    { name: "Not Available", value: handWashGraphFilter?.NoAvailable || 0 },
  ];

  const total = data.reduce((sum, item) => sum + item.value, 0);

  const exportPDF = async (isModal = false) => {
    const targetRef = isModal ? modalChartContainerRef : chartContainerRef;
    if (!targetRef.current) return;

    const canvas = await html2canvas(targetRef.current, { scale: 2 });
    const imgData = canvas.toDataURL("image/png");

    const pdf = new jsPDF("landscape", "pt", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

    pdf.addImage(imgData, "PNG", 0, 40, pdfWidth, pdfHeight);
    pdf.save("handwashing-facility-chart.pdf");
  };

  const ChartContent = () => (
    <div className="w-full max-w-md">
      {/* Total Count with Icon */}
      <div className="flex items-center justify-center mb-4">
        <div className="flex items-center gap-2">
          <img src="users-cog.png" alt="Handwashing Icon" className="w-8 h-8" />
          <span className="text-4xl font-bold text-teal-600">
            {total < 1000
              ? formatCount(total)
              : `${(total / 1000).toFixed(1)}K`}
          </span>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-4 px-14">
        <div className="h-8 bg-gray-200 rounded-full overflow-hidden flex">
          {" "}
          {/* Increased height from h-6 to h-8 */}
          {data.map((item, index) => {
            const percentage = total > 0 ? (item.value / total) * 100 : 0;
            const isAvailable = item.name.toLowerCase() === "available";

            return (
              <div
                key={item.name}
                className={`h-full ${
                  isAvailable ? "bg-teal-600" : "bg-yellow-600"
                }`}
                style={{ width: `${percentage}%` }}
              />
            );
          })}
        </div>
      </div>

      {/* Legend with Counts */}
      <div className="flex justify-center space-x-16">
        {data.map((item) => {
          const isAvailable = item.name.toLowerCase() === "available";

          return (
            <div key={item.name} className="text-center">
              <div
                className={`text-2xl font-bold ${
                  isAvailable ? "text-teal-600" : "text-gray-400"
                }`}
              >
                {item.value < 1000
                  ? formatCount(item.value)
                  : `${(item.value / 1000).toFixed(1)}K`}
              </div>
              <div className="text-sm text-[#606060]">{item.name}</div>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <>
      {/* Blue Header */}
      <div className="text-xl my-2 p-0 text-[#3A3A3A] font-bold uppercase text-center">
        Handwashing Facility Availability
      </div>

      <div className="bg-white rounded-b-lg p-3">
        <div className="h-80 flex items-start justify-center p-4">
          <div
            ref={chartContainerRef}
            className="cursor-pointer"
            onClick={() => setShowModal(true)}
            title="Click to view fullscreen"
          >
            <ChartContent />
          </div>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-[9999] bg-white flex flex-col items-center justify-center">
          <button
            className="absolute top-4 left-6 text-3xl font-bold text-gray-600 hover:text-black"
            onClick={() => setShowModal(false)}
          >
            ×
          </button>

          <div className="absolute top-4 right-6">
            <div className="relative inline-block text-left">
              <div className="origin-top-right absolute right-0 mt-2 w-40 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                <div className="py-1">
                  <button
                    onClick={() => exportPDF(true)}
                    className="w-full px-4 py-2 text-sm text-[#4A90E2] font-bold hover:bg-gray-100"
                  >
                    Export PDF
                  </button>
                </div>
              </div>
            </div>
          </div>

          <h2 className="text-xl font-semibold mb-4 text-[#4A90E2] mt-8 uppercase">
            Handwashing Facility Availability
          </h2>

          <div
            ref={modalChartContainerRef}
            className="w-[90vw] max-w-2xl bg-white p-8 rounded-lg"
          >
            <ChartContent />
          </div>
        </div>
      )}
    </>
  );
};

export default HandwashingFacilityChart;
